import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { trustCopy, trustPillars, trustMedia } from "../config/media";

const EASE = [0.16, 1, 0.3, 1];

export const TrustSection = () => {
  return (
    <section
      data-testid="trust-section"
      className="bg-[var(--strollee-bg)] px-6 lg:px-10 py-20 lg:py-24"
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-120px" }}
        transition={{ duration: 0.8, ease: EASE }}
        className="flex items-end justify-between gap-12"
      >
        <div className="max-w-[560px]">
          <p
            data-testid="trust-eyebrow"
            className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--strollee-accent)]"
          >
            {trustCopy.eyebrow}
          </p>
          <h2 className="mt-4 text-[36px] lg:text-[48px] font-semibold leading-[1.1] tracking-[-0.03em] text-[var(--strollee-ink)]">
            {trustCopy.headlineLines.map((line) => (
              <span key={line} className="block">
                {line}
              </span>
            ))}
          </h2>
        </div>
        <p className="max-w-[340px] pb-2 text-base leading-relaxed text-[var(--strollee-muted)]">
          {trustCopy.support}
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 1.015 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 1.1, ease: [0.25, 1, 0.5, 1] }}
        className="mt-10 lg:mt-12"
      >
        <div className="relative h-[50vh] lg:h-[56vh] overflow-hidden rounded-[var(--strollee-radius-hero)] bg-[var(--strollee-line)]/40">
          <img
            src={trustMedia.url}
            alt={trustMedia.alt}
            data-testid="trust-media"
            className="absolute inset-0 h-full w-full object-cover"
            draggable={false}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#171713]/55 via-transparent to-transparent" />
          <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-10 p-8 lg:p-12">
            <h3
              data-testid="trust-media-statement"
              className="text-[28px] lg:text-[36px] font-semibold leading-[1.12] tracking-[-0.02em] text-white"
            >
              {trustCopy.mediaStatement.map((line) => (
                <span key={line} className="block">
                  {line}
                </span>
              ))}
            </h3>
            <a
              href="#engineering"
              data-testid="trust-cta"
              className="group mb-1 inline-flex shrink-0 items-center gap-2 rounded-full border border-white/30 bg-white/10 px-5 py-2.5 text-[13px] font-semibold tracking-wide text-white backdrop-blur-md transition-all duration-200 hover:bg-white hover:text-[var(--strollee-ink)]"
            >
              {trustCopy.cta}
              <ArrowRight
                size={15}
                strokeWidth={2.2}
                className="transition-transform duration-200 group-hover:translate-x-1"
              />
            </a>
          </div>
        </div>
      </motion.div>

      <div className="mt-12 lg:mt-14 grid grid-cols-4 gap-8 lg:gap-12">
        {trustPillars.map((p, i) => (
          <motion.div
            key={p.id}
            data-testid={`trust-pillar-${p.id}`}
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: EASE, delay: i * 0.12 }}
            className="group border-t border-[var(--strollee-line)] pt-6"
          >
            <p className="text-[13px] font-semibold tracking-[0.18em] text-[var(--strollee-muted)]/70 transition-colors duration-200 group-hover:text-[var(--strollee-accent)]">
              {p.number}
            </p>
            <h4 className="mt-3 text-base lg:text-lg font-semibold tracking-[-0.01em] text-[var(--strollee-ink)]">
              {p.label}
            </h4>
            <p className="mt-3 text-sm leading-relaxed text-[var(--strollee-muted)]">
              {p.copy}
            </p>
          </motion.div>
        ))}
      </div>
    </section>
  );
};
